import { Component, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  ApplicationSearchSearchResult,
  ApplicationSearchSearchResultType,
  SearchService,
} from '@crm/api';
import { debounceTime, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { AuthService } from './auth/auth.service';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { StickyShadowDirective } from './shared/sticky-shadow.directive';

/**
 * Whether the side menu stands folded, remembered in the browser rather than in the account: the
 * same person wants it folded on a phone, where the width is counted, and open on a desktop
 * (issue #49). Only the button counts as a decision — the automatic fold below the `md` width is
 * the window's doing, not the user's, and is not written down.
 */
const MENU_COLLAPSED_KEY = 'menu.collapsed';

function readMenuCollapsed(): boolean {
  try {
    return localStorage.getItem(MENU_COLLAPSED_KEY) === '1';
  } catch {
    // Private window, or site data refused: the menu opens as it always did.
    return false;
  }
}

/** Ant Design's `md`: below it the menu folds on its own, however it was left. */
const MD_WIDTH_PX = 768;

function isNarrowWindow(): boolean {
  return window.innerWidth < MD_WIDTH_PX;
}

function writeMenuCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(MENU_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Nothing to do: the choice simply does not outlive the page.
  }
}

@Component({
  imports: [
    RouterLink,
    RouterOutlet,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    NzInputModule,
    NzButtonModule,
    ReactiveFormsModule,
    NzAutocompleteModule,
    NzDropDownModule,
    AsyncPipe,
    NzTagComponent,
    NgOptimizedImage,
    NzTooltipDirective,
    StickyShadowDirective,
  ],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  private readonly searchService = inject(SearchService);
  private readonly router = inject(Router);
  public readonly authService = inject(AuthService);

  /** What the user asked for, which the window may fold further but never unfold against. */
  private menuCollapsedPreference = readMenuCollapsed();

  isCollapsed = this.menuCollapsedPreference || isNarrowWindow();
  searchControl = new FormControl('');
  searchResults$: Observable<ApplicationSearchSearchResult[]>;
  isLoading = false;

  constructor() {
    this.searchResults$ = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.isLoading = true)),
      switchMap((query) => {
        if (!query || query.length < 2) {
          this.isLoading = false;
          return of([]);
        }
        return this.searchService.searchGet({ q: query }).pipe(
          map((response) => response.results || []),
          tap(() => (this.isLoading = false))
        );
      })
    );
  }

  /** The one deliberate fold: pressing the trigger, which is therefore the one remembered. */
  toggleMenu(): void {
    this.isCollapsed = !this.isCollapsed;
    this.menuCollapsedPreference = this.isCollapsed;
    writeMenuCollapsed(this.isCollapsed);
  }

  /**
   * A narrow window folds the menu whatever the user asked for, and widening it again restores
   * their choice rather than simply unfolding.
   *
   * This is the sider's own `nzBreakpoint` behaviour, taken over: left to the sider, its initial
   * "unfolded" on a wide window silently overrode the remembered fold, and the one-way binding
   * then had no change to push back — the state said folded while the menu stood open.
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.isCollapsed = isNarrowWindow() || this.menuCollapsedPreference;
  }

  logout(): void {
    this.authService.logout();
    void this.router.navigate(['/login']);
  }

  goToProfile(): void {
    void this.router.navigate(['/profile']);
  }

  onProfileKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.goToProfile();
    }
  }

  onLogoutKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.logout();
    }
  }

  onOptionSelected(result: ApplicationSearchSearchResult): void {
    // Customer or prospect, the record is the same one: what the result is says which parts of it
    // are worth showing, not where it lives.
    void this.router.navigate(['/contacts/show'], {
      queryParams: { id: result.id },
    });
    this.searchControl.setValue('', { emitEvent: false });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === '/' && !this.isTargetingInput(event)) {
      event.preventDefault();
      this.searchInput.nativeElement.focus();
    }
  }

  private isTargetingInput(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    return (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    );
  }

  trackSearchResult(
    index: number,
    result: ApplicationSearchSearchResult
  ): string {
    return `${result.type ?? 'unknown'}-${result.id ?? index}`;
  }

  protected readonly ApplicationSearchSearchResultType =
    ApplicationSearchSearchResultType;
}
